import { db, type FocusSession, type MosDB, newId } from "./db";
import { extendedPlan, finishPatch, isStale, MAX_PLANNED_MIN } from "./focus-timer";
import { createTask } from "./planner";
import { nextUpdatedAt, save, update } from "./store";

// Focus writes (FC-1..4): Dexie + outbox first, like every other table.

export class FocusRunningError extends Error {
	constructor() {
		super("A focus session is already running");
		this.name = "FocusRunningError";
	}
}

export const DURATIONS = [15, 25, 45, 60, 90] as const;

/** The running session, if any (D56). */
export async function runningSession(database: MosDB = db): Promise<FocusSession | undefined> {
	return database.focus_sessions.filter((s) => !s.ended_at && !s.deleted_at).first();
}

/**
 * Starts a session. One at a time: a session abandoned long ago is closed
 * first (not counted as completed); a live one blocks the new start.
 */
export async function startFocus(
	input: { planned_min: number; task_id?: string | null; track_id?: string | null },
	database: MosDB = db,
	now: Date = new Date(),
): Promise<FocusSession> {
	if (
		!Number.isInteger(input.planned_min) ||
		input.planned_min < 1 ||
		input.planned_min > MAX_PLANNED_MIN
	)
		throw new RangeError(`Planned time must be 1-${MAX_PLANNED_MIN} minutes`);
	return database.transaction("rw", database.focus_sessions, database.outbox, async () => {
		const running = await runningSession(database);
		if (running) {
			if (!isStale(running, now)) throw new FocusRunningError();
			await update("focus_sessions", running.id, finishPatch(running, now), database);
		}
		const stamp = nextUpdatedAt();
		return save(
			"focus_sessions",
			{
				id: newId(),
				task_id: input.task_id ?? null,
				track_id: input.track_id ?? null,
				planned_min: input.planned_min,
				started_at: now.toISOString(),
				ended_at: null,
				completed: false,
				distraction_count: 0,
				created_at: stamp,
				updated_at: stamp,
				deleted_at: null,
			},
			database,
		);
	});
}

/** Ends the session now (or at its planned end if it was abandoned). */
export function finishFocus(
	id: string,
	database: MosDB = db,
	now: Date = new Date(),
): Promise<FocusSession> {
	return database.transaction("rw", database.focus_sessions, database.outbox, async () => {
		const s = await database.focus_sessions.get(id);
		if (!s || s.deleted_at) throw new Error(`focus_sessions ${id} not found`);
		if (s.ended_at) return s;
		return update("focus_sessions", id, finishPatch(s, now), database);
	});
}

/** Adds five minutes to a running session. */
export function extendFocus(id: string, database: MosDB = db): Promise<FocusSession> {
	return database.transaction("rw", database.focus_sessions, database.outbox, async () => {
		const s = await database.focus_sessions.get(id);
		if (!s || s.deleted_at || s.ended_at) throw new Error("Only a running session can be extended");
		return update("focus_sessions", id, { planned_min: extendedPlan(s) }, database);
	});
}

/** Logs what pulled attention away and bumps the session's count (FC-3). */
export function logDistraction(
	sessionId: string,
	note = "",
	database: MosDB = db,
	now: Date = new Date(),
): Promise<void> {
	return database.transaction(
		"rw",
		database.focus_sessions,
		database.distractions,
		database.outbox,
		async () => {
			const s = await database.focus_sessions.get(sessionId);
			if (!s || s.deleted_at) throw new Error(`focus_sessions ${sessionId} not found`);
			const stamp = nextUpdatedAt();
			await save(
				"distractions",
				{
					id: newId(),
					focus_session_id: sessionId,
					note: note.trim().slice(0, 200),
					occurred_at: now.toISOString(),
					created_at: stamp,
					updated_at: stamp,
					deleted_at: null,
				},
				database,
			);
			await update(
				"focus_sessions",
				sessionId,
				{ distraction_count: Math.min(1000, s.distraction_count + 1) },
				database,
			);
		},
	);
}

/**
 * Captures a thought without leaving focus (FC-4, D57): an Inbox task tagged
 * "parked", to be triaged later.
 */
export async function parkThought(text: string, database: MosDB = db) {
	const title = text.trim();
	if (!title) throw new RangeError("Write the thought first");
	return createTask({ title: title.slice(0, 500), tags: ["parked"] }, database);
}
